import {
  Dispatch,
  RefObject,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { IMAGE_STATUS } from "../../../../common/constants";
import { AFrameSky, ImageLoadingHook } from "../types";

export const useImageLoading = (
  skyRef: RefObject<AFrameSky | null>,
  currentFrameIndex: number,
  setCurrentImageStatus: Dispatch<SetStateAction<IMAGE_STATUS>>,
  setLoadedImageIndex: Dispatch<SetStateAction<number | false>>,
  isSplitView: boolean
): ImageLoadingHook => {
  const loadedImagesIndicesRef = useRef<Set<number>>(new Set());
  const [imgWidth, setImgWidth] = useState<number>(7680);
  const [imgHeight, setImgHeight] = useState<number>(3840);

  useEffect(() => {
    const sky = skyRef.current;

    if (!loadedImagesIndicesRef.current.has(currentFrameIndex)) {
      setCurrentImageStatus((prev) =>
        prev === IMAGE_STATUS.LOADING ? prev : IMAGE_STATUS.INITIAL
      );
    }

    const timer = setTimeout(() => {
      if (!loadedImagesIndicesRef.current.has(currentFrameIndex)) {
        setCurrentImageStatus(IMAGE_STATUS.LOADING);
      }
    }, 200);

    const handleError = (): void => {
      setCurrentImageStatus(IMAGE_STATUS.ERROR);
    };

    const handleMaterialTextureLoaded = (e: any): void => {
      const image = e.target?.components?.material?.material?.map?.source?.data;
      if (image) {
        setImgWidth(image.naturalWidth);
        setImgHeight(image.naturalHeight);
      }

      clearTimeout(timer);

      setCurrentImageStatus(IMAGE_STATUS.LOADED);
      setLoadedImageIndex(currentFrameIndex);
      loadedImagesIndicesRef.current.add(currentFrameIndex);
    };

    if (sky) {
      sky.addEventListener(
        "materialtextureloaded",
        handleMaterialTextureLoaded
      );
      sky.addEventListener("material-error", handleError);
    }

    return () => {
      if (sky) {
        sky.removeEventListener("material-error", handleError);
        sky.removeEventListener(
          "materialtextureloaded",
          handleMaterialTextureLoaded
        );
        clearTimeout(timer);
      }
    };
  }, [
    currentFrameIndex,
    setCurrentImageStatus,
    setLoadedImageIndex,
    skyRef,
    isSplitView,
  ]);

  return {
    loadedImagesIndicesRef,
    imgWidth,
    imgHeight,
  };
};
